import {
  Box,
  BoxProps,
  Circle,
  Flex,
  Icon,
  Image,
  Link,
} from "@chakra-ui/react";
import { AttentionThickIcon } from "../icons/AttentionThickIcon";
import { useSlideInMenu } from "../slideInMenu/hooks/useSlideInMenu";
import { useAccountData } from "../../hooks/useAccountData";
import { FunnelContextArea } from "../funnel/types";

export type AvatarSize = "sm" | "md" | "lg" | "xl";
interface AvatarProps extends BoxProps {
  size?: AvatarSize;
  interactive?: boolean;
  active?: boolean;
  imageSrc?: string;
  username?: string;
  isOnline?: boolean;
  isGold?: boolean;
  showAlert?: boolean;
  showRing?: boolean;
  customSize?: number;
  ringStroke?: string;
  openProfileOnClick?: boolean;
  contextArea?: FunnelContextArea;
  usePointerEvents?: boolean;
}

export function Avatar({
  size = "md",
  interactive,
  active,
  imageSrc = `${window.gc.global.assets_url}/static/images/avatars/no-avatar.png`,
  username = "",
  isOnline = false,
  isGold,
  showAlert = false,
  showRing = true,
  customSize,
  ringStroke,
  openProfileOnClick,
  contextArea,
  usePointerEvents = true,
  ...boxProps
}: AvatarProps) {
  const { openUserProfile } = useSlideInMenu();
  const accountData = useAccountData();
  const isUserProfileEnabled = accountData?.features?.["user_profile"];

  const avatarSizes = {
    sm: 24,
    md: 32,
    lg: 40,
    xl: 64,
  };

  const avatarRingSizes = {
    sm: 32,
    md: 40,
    lg: 48,
    xl: 72,
  };

  const avatarSize = (customSize ?? avatarSizes[size]) + "px";
  const avatarRingSize = customSize ? customSize + 12 : avatarRingSizes[size];

  return (
    <Link
      onClick={
        openProfileOnClick && username !== "" && isUserProfileEnabled
          ? () => openUserProfile(username, { area: contextArea })
          : undefined
      }
      pointerEvents={usePointerEvents ? "auto" : "none"}
    >
      <Flex
        position="relative"
        alignItems="center"
        justifyContent="center"
        maxHeight={avatarRingSize}
        maxWidth={avatarRingSize}
        width={showRing ? "fit-content" : avatarSize}
        height={showRing ? "fit-content" : avatarSize}
        {...boxProps}
      >
        <Flex
          position="absolute"
          width={avatarSize}
          height={avatarSize}
          justifyContent="center"
          alignItems="center"
          borderRadius="50%"
          pointerEvents="none"
        >
          <Image
            loading="lazy"
            src={imageSrc.replace("no-avatar.png", "webNext-no-avatar.svg")}
            alt={`Avatar image for ${username}`}
            boxSize={avatarSize}
            borderRadius="50%"
            pointerEvents="none"
          />
        </Flex>
        {showRing && (
          <AvatarRing
            size={avatarRingSize}
            grey={!isGold}
            interactive={interactive}
            active={false}
            ringStroke={ringStroke}
          />
        )}
        {isOnline && (
          <Box position="absolute" top="0" right="0">
            <Circle size="12px" bg="charcoal.charcoal">
              <Circle size="10px" bg="success.500" />
            </Circle>
          </Box>
        )}

        {showAlert && (
          <Box
            position="absolute"
            top="0"
            right="0"
            transform="translate(25%, -25%)"
          >
            <Circle
              size="16px"
              bg="warning.600"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <AttentionThickIcon
                color="charcoal.charcoal"
                boxSize="16px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              />
            </Circle>
          </Box>
        )}
      </Flex>
    </Link>
  );
}

interface AvatarRingProps {
  size: number;
  grey?: boolean;
  interactive?: boolean;
  active?: boolean;
  ringStroke?: string;
}

function AvatarRing({
  size,
  grey,
  interactive,
  active,
  ringStroke,
}: AvatarRingProps) {
  const isSmall = size === 32;

  const strokeWidthInt = ringStroke
    ? parseInt(ringStroke.replace("px", ""))
    : undefined;
  return (
    <Icon
      width={size + "px"}
      height={size + "px"}
      strokeWidth={active ? "4" : isSmall ? "1.5" : "2"}
      transform={active ? "scale(1.1)" : "scale(1)"}
      _hover={
        interactive
          ? {
              transform: "scale(1.1)",
              strokeWidth: "4",
            }
          : {}
      }
      transition="all .2s linear"
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={size / 2 - (strokeWidthInt ?? (!isSmall ? 2 : 4))}
        stroke={grey ? "url(#ring-grey)" : "url(#ring-blaze)"}
        strokeWidth={ringStroke ?? "inherit"}
        paintOrder="stroke"
      />

      <defs>
        <linearGradient
          id="ring-grey"
          x1={size - (strokeWidthInt ?? 2)}
          y1={strokeWidthInt ?? 2}
          x2={strokeWidthInt ?? 2}
          y2={size - (strokeWidthInt ?? 2)}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#A1A1A2" />
          <stop offset="1" stopColor="#434445" />
        </linearGradient>
        <linearGradient
          id="ring-blaze"
          x1={size - 2}
          y1="2"
          x2="2"
          y2={size - 2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FF5A2C" />
          <stop offset="1" stopColor="#6E1800" />
        </linearGradient>
      </defs>
    </Icon>
  );
}
